/**
 * Material Formula Registry
 *
 * Maps writeup row IDs → material quantity calculations.
 * Constants are standard industry values (density in lb/ft³, coverage rates, etc.)
 * These are physics — they don't vary by company. Per-company pricing is in PricingItem.
 *
 * Formula pattern for volume→tons:  area_sf × (depth_inches / 12) × density_lb_per_ft3 / 2000
 * Formula pattern for area→units:   area_sf × application_rate
 * Formula pattern for length→units: length (direct passthrough)
 */

// ── Material density constants (lb/ft³ unless noted) ──

const DENSITY = {
  ASPHALT: 144,               // lb/ft³ — hot mix asphalt
  CLASS_2_BASE: 165,          // lb/ft³ — aggregate base
  CRACK_FILL_HOT: 2.568,      // count/ft³
  CRACK_FILL_COLD: 1.516,     // gal/ft³
} as const

// ── Concrete curb/gutter cross-section factors (yd³ per linear foot) ──
// These encode the exact cross-section volume of each curb profile.
// All include a 5% waste factor (× 1.05).
const CURB_FACTOR = {
  CURB_6_GUTTER_18: 0.05,     // 6" curb & 18" gutter
  CURB_6_GUTTER_24: 0.06,     // 6" curb & 24" gutter
  CURB_8_GUTTER_18: 0.058,    // 8" curb & 18" gutter
  CURB_8_GUTTER_24: 0.071,    // 8" curb & 24" gutter
  CURB_ONLY_6: 0.023,         // curb only — 6" face, 6" footing
  CURB_ONLY_8: 0.044,         // curb only — 8" face, 12" footing
  ROLLED_CURB: 0.042,         // rolled curb — 2.25' wide
  V_GUTTER: 0.056,            // V gutter — 3' wide
} as const

const RATES = {
  SS1H_TACK: 0.0111,          // gallon/ft² application rate
} as const

// Sealcoat application rates (gal per SF) by surface type and coat count.
// 2nd coat is lighter because the surface is already sealed by the first.
// Keys support both short ('S') and full ('Smooth') forms to match dropdown values.
const SEALCOAT_RATE: Record<string, { 1: number; 2: number }> = {
  Smooth: { 1: 0.019, 2: 0.024 },
  S:      { 1: 0.019, 2: 0.024 },
  Medium: { 1: 0.022, 2: 0.027 },
  M:      { 1: 0.022, 2: 0.027 },
  Rough:  { 1: 0.030, 2: 0.035 },
  R:      { 1: 0.030, 2: 0.035 },
}

// ── Types ──

export interface MaterialCalculation {
  formulaKey: string           // Stable key matching PricingItem.formulaKey (e.g., 'asphalt', 'sealcoat')
  materialName: string         // Default display name (used as fallback label only)
  quantity: number             // Computed quantity in the material's native unit
  unit: string                 // Unit for display (ton, sq ft, gallon, ft, cubic yard, count)
  sourceRowId: string          // Which writeup row produced this
}

export interface FormulaEntry {
  rowId: string                // Writeup row ID (e.g., 'a-r1')
  section: string              // Which estimate section this belongs to (asphalt, grading, seal-coat, concrete)
  materials: Array<{
    formulaKey: string         // Stable key for PricingItem lookup (never changes)
    materialName: string       // Default display name (tenant can rename)
    unit: string
    calculate: (fields: Record<string, number>, stringFields?: Record<string, string>) => number
    requiredFields: string[]   // Placeholder names that must have values
  }>
}

// ── Helper: volume → tons ──

function volumeToTons(areaSF: number, depthInches: number, densityLbPerFt3: number): number {
  return areaSF * (depthInches / 12) * densityLbPerFt3 / 2000
}

// ── Helper: volume → cubic yards ──

function volumeToCubicYards(areaSF: number, depthInches: number): number {
  return areaSF * (depthInches / 12) / 27
}

// ── Helper: curb/gutter → cubic yards (factor already includes 5% waste) ──

function curbVolume(lengthFt: number, factorYd3PerFt: number): number {
  return lengthFt * factorYd3PerFt
}

// ── Formula Registry ──

export const FORMULA_REGISTRY: FormulaEntry[] = [

  // ═══════════════════════════════════════════
  // ASPHALT — New Construction (main rows)
  // ═══════════════════════════════════════════

  {
    rowId: 'a-r1',
    section: 'asphalt',
    materials: [{
      formulaKey: 'class-2-base',
      materialName: 'Class 2 Base',
      unit: 'ton',
      requiredFields: ['depth', 'area'],
      calculate: ({ area, depth }) => volumeToTons(area, depth, DENSITY.CLASS_2_BASE),
    }],
  },
  {
    rowId: 'a-r2',
    section: 'asphalt',
    materials: [{
      formulaKey: 'weed-killer',
      materialName: 'Weed Killer',
      unit: 'square foot',
      requiredFields: ['area'],
      calculate: ({ area }) => area,
    }],
  },
  {
    rowId: 'a-r3',
    section: 'asphalt',
    materials: [{
      formulaKey: 'header-board-2x4',
      materialName: 'Header Board (2×4)',
      unit: 'ft',
      requiredFields: ['length'],
      calculate: ({ length }) => length,
    }],
  },
  {
    rowId: 'a-r4',
    section: 'asphalt',
    materials: [{
      formulaKey: 'header-board-2x6',
      materialName: 'Header Board (2×6)',
      unit: 'ft',
      requiredFields: ['length'],
      calculate: ({ length }) => length,
    }],
  },
  {
    rowId: 'a-r5',
    section: 'asphalt',
    materials: [{
      formulaKey: 'header-board-2x8',
      materialName: 'Header Board (2×8)',
      unit: 'ft',
      requiredFields: ['length'],
      calculate: ({ length }) => length,
    }],
  },
  {
    rowId: 'a-r6',
    section: 'asphalt',
    materials: [{
      formulaKey: 'asphalt',
      materialName: 'Asphalt',
      unit: 'ton',
      requiredFields: ['depth', 'area'],
      calculate: ({ area, depth }) => volumeToTons(area, depth, DENSITY.ASPHALT),
    }],
  },
  // a-r7: Grade and compact — no material cost (labor/equipment only)

  // ═══════════════════════════════════════════
  // ASPHALT — Remove & Replace
  // ═══════════════════════════════════════════

  {
    rowId: 'a-sr1',
    section: 'asphalt-rnr',
    materials: [{
      formulaKey: 'saw-cut',
      materialName: 'Saw Cut',
      unit: 'ft',
      requiredFields: ['length'],
      calculate: ({ length }) => length,
    }],
  },
  {
    rowId: 'a-sr2',
    section: 'asphalt-rnr',
    materials: [{
      formulaKey: 'saw-cut',
      materialName: 'Saw Cut',
      unit: 'ft',
      requiredFields: ['length'],
      calculate: ({ length }) => length,
    }],
  },
  {
    rowId: 'a-sr16',
    section: 'asphalt-rnr',
    materials: [{
      formulaKey: 'class-2-base',
      materialName: 'Class 2 Base',
      unit: 'ton',
      requiredFields: ['depth', 'area'],
      calculate: ({ area, depth }) => volumeToTons(area, depth, DENSITY.CLASS_2_BASE),
    }],
  },
  // a-sr3: Remove asphalt — no material (load/cut/haul only)
  // a-sr4: Mill out asphalt — no material (load/cut/haul only)
  // a-sr5: Pulverize — no material
  // a-sr6: Mill out dirt — no material
  // a-sr7: Remove concrete slab — no material (removal only)
  // a-sr8: Remove curb — no material (removal only)
  // a-sr9: Remove curb and gutter — no material
  // a-sr10: Remove rolled curb — no material
  // a-sr11: Grade and compact (base/asphalt) — no material
  // a-sr15: Grade and compact (asphalt) — no material
  {
    rowId: 'a-sr12',
    section: 'asphalt-rnr',
    materials: [{
      formulaKey: 'ss1h-tack',
      materialName: 'SS1H-Tack',
      unit: 'gallon',
      requiredFields: ['area'],
      calculate: ({ area }) => area * RATES.SS1H_TACK,
    }],
  },
  {
    rowId: 'a-sr13',
    section: 'asphalt-rnr',
    materials: [{
      formulaKey: 'asphalt',
      materialName: 'Asphalt',
      unit: 'ton',
      requiredFields: ['depth', 'area'],
      calculate: ({ area, depth }) => volumeToTons(area, depth, DENSITY.ASPHALT),
    }],
  },
  {
    rowId: 'a-sr14',
    section: 'asphalt-rnr',
    materials: [{
      formulaKey: 'asphalt',
      materialName: 'Asphalt',
      unit: 'ton',
      requiredFields: ['depth', 'area'],
      calculate: ({ area, depth }) => volumeToTons(area, depth, DENSITY.ASPHALT),
    }],
  },

  // ═══════════════════════════════════════════
  // ASPHALT — Overlay
  // ═══════════════════════════════════════════

  {
    rowId: 'a-or1',
    section: 'asphalt-overlay',
    materials: [{
      formulaKey: 'ss1h-tack',
      materialName: 'SS1H-Tack',
      unit: 'gallon',
      requiredFields: ['area'],
      calculate: ({ area }) => area * RATES.SS1H_TACK,
    }],
  },
  {
    rowId: 'a-or2',
    section: 'asphalt-overlay',
    materials: [{
      formulaKey: 'asphalt',
      materialName: 'Asphalt',
      unit: 'ton',
      requiredFields: ['area', 'depth'],
      calculate: ({ area, depth }) => volumeToTons(area, depth, DENSITY.ASPHALT),
    }],
  },
  // a-or3: Place ground sterilizer — no variable material
  {
    rowId: 'a-or4',
    section: 'asphalt-overlay',
    materials: [{
      formulaKey: 'asphalt',
      materialName: 'Asphalt',
      unit: 'ton',
      requiredFields: ['depth', 'area'],
      calculate: ({ area, depth }) => volumeToTons(area, depth, DENSITY.ASPHALT),
    }],
  },
  // a-or5: Clean with blowers — no material
  {
    rowId: 'a-or6',
    section: 'asphalt-overlay',
    materials: [
      {
        formulaKey: 'petromat',
        materialName: 'Petromat',
        unit: 'square foot',
        requiredFields: ['area'],
        calculate: ({ area }) => area,
      },
      {
        formulaKey: 'asphalt',
        materialName: 'Asphalt',
        unit: 'ton',
        requiredFields: ['area', 'depth'],
        calculate: ({ area, depth }) => volumeToTons(area, depth, DENSITY.ASPHALT),
      },
    ],
  },
  // a-or7: Grade and compact — no material
  // a-or8: Header cut — removal/disposal only
  // a-or9: Clean with blowers — no material
  // a-or10: Place AR8000 hot oil binder — no variable material
  // a-or11: Mill out — removal only

  // ═══════════════════════════════════════════
  // GRADING
  // ═══════════════════════════════════════════

  // g-r2: Grade and compact — no material
  {
    rowId: 'g-r3',
    section: 'grading',
    materials: [{
      formulaKey: 'weed-killer',
      materialName: 'Weed Killer',
      unit: 'square foot',
      requiredFields: ['area'],
      calculate: ({ area }) => area,
    }],
  },
  {
    rowId: 'g-r4',
    section: 'grading',
    materials: [{
      formulaKey: 'class-2-base',
      materialName: 'Class 2 Base',
      unit: 'ton',
      requiredFields: ['depth', 'area'],
      calculate: ({ area, depth }) => volumeToTons(area, depth, DENSITY.CLASS_2_BASE),
    }],
  },

  // ═══════════════════════════════════════════
  // CONCRETE
  // ═══════════════════════════════════════════

  // c-r1: Set forms — no material
  {
    rowId: 'c-r2',
    section: 'concrete',
    materials: [{
      formulaKey: 'concrete',
      materialName: 'Concrete',
      unit: 'cubic yard',
      requiredFields: ['length'],
      // 6" curb & 24" gutter — 0.06 yd³/ft (includes 5% waste)
      calculate: ({ length }) => curbVolume(length, CURB_FACTOR.CURB_6_GUTTER_24),
    }],
  },
  {
    rowId: 'c-r3',
    section: 'concrete',
    materials: [{
      formulaKey: 'concrete',
      materialName: 'Concrete',
      unit: 'cubic yard',
      requiredFields: ['area', 'depth'],
      calculate: ({ area, depth }) => volumeToCubicYards(area, depth),
    }],
  },
  {
    rowId: 'c-r3b',
    section: 'concrete',
    materials: [{
      formulaKey: 'concrete',
      materialName: 'Concrete',
      unit: 'cubic yard',
      requiredFields: ['area'],
      // Sidewalks: standard 4" thickness
      calculate: ({ area }) => volumeToCubicYards(area, 4),
    }],
  },
  // c-r4: Grade and compact — no material
  // c-r5: Remove and Replace — no material
  {
    rowId: 'c-r6',
    section: 'concrete',
    materials: [{
      formulaKey: 'concrete',
      materialName: 'Concrete',
      unit: 'cubic yard',
      requiredFields: ['length'],
      // 6" curb & 18" gutter — 0.05 yd³/ft (includes 5% waste)
      calculate: ({ length }) => curbVolume(length, CURB_FACTOR.CURB_6_GUTTER_18),
    }],
  },
  {
    rowId: 'c-r7',
    section: 'concrete',
    materials: [{
      formulaKey: 'concrete',
      materialName: 'Concrete',
      unit: 'cubic yard',
      requiredFields: ['length'],
      // 8" curb & 18" gutter — 0.058 yd³/ft (includes 5% waste)
      calculate: ({ length }) => curbVolume(length, CURB_FACTOR.CURB_8_GUTTER_18),
    }],
  },
  {
    rowId: 'c-r8',
    section: 'concrete',
    materials: [{
      formulaKey: 'concrete',
      materialName: 'Concrete',
      unit: 'cubic yard',
      requiredFields: ['length'],
      // 8" curb & 24" gutter — 0.071 yd³/ft (includes 5% waste)
      calculate: ({ length }) => curbVolume(length, CURB_FACTOR.CURB_8_GUTTER_24),
    }],
  },
  {
    rowId: 'c-r9',
    section: 'concrete',
    materials: [{
      formulaKey: 'concrete',
      materialName: 'Concrete',
      unit: 'cubic yard',
      requiredFields: ['length'],
      // Curb only — 6" face, 6" footing — 0.023 yd³/ft (includes 5% waste)
      calculate: ({ length }) => curbVolume(length, CURB_FACTOR.CURB_ONLY_6),
    }],
  },
  {
    rowId: 'c-r10',
    section: 'concrete',
    materials: [{
      formulaKey: 'concrete',
      materialName: 'Concrete',
      unit: 'cubic yard',
      requiredFields: ['length'],
      // Curb only — 8" face, 12" footing — 0.044 yd³/ft (includes 5% waste)
      calculate: ({ length }) => curbVolume(length, CURB_FACTOR.CURB_ONLY_8),
    }],
  },
  {
    rowId: 'c-r11',
    section: 'concrete',
    materials: [{
      formulaKey: 'concrete',
      materialName: 'Concrete',
      unit: 'cubic yard',
      requiredFields: ['length'],
      // V gutter — 0.056 yd³/ft (includes 5% waste)
      calculate: ({ length }) => curbVolume(length, CURB_FACTOR.V_GUTTER),
    }],
  },
  {
    rowId: 'c-r12',
    section: 'concrete',
    materials: [{
      formulaKey: 'concrete',
      materialName: 'Concrete',
      unit: 'cubic yard',
      requiredFields: ['length'],
      // Rolled curb — 2.25' wide — 0.042 yd³/ft (includes 5% waste)
      calculate: ({ length }) => curbVolume(length, CURB_FACTOR.ROLLED_CURB),
    }],
  },
  // c-r13: Custom curb — manual entry
  {
    rowId: 'c-r14',
    section: 'concrete',
    materials: [{
      formulaKey: 'rebar-12oc',
      materialName: 'Rebar 12" OC',
      unit: 'ea',
      requiredFields: ['area'],
      // Two-way 12" OC: area × 2 LF/SF ÷ 20 ft/bar × 1.12 waste factor
      calculate: ({ area }) => Math.round(area * 2 / 20 * 1.12),
    }],
  },
  {
    rowId: 'c-r15',
    section: 'concrete',
    materials: [{
      formulaKey: 'rebar-18oc',
      materialName: 'Rebar 18" OC',
      unit: 'ea',
      requiredFields: ['area'],
      // Two-way 18" OC: area × 1.333 LF/SF ÷ 20 ft/bar × 1.12 waste factor
      calculate: ({ area }) => Math.round(area * (4 / 3) / 20 * 1.12),
    }],
  },
  {
    rowId: 'c-r16',
    section: 'concrete',
    materials: [{
      formulaKey: 'rebar-24oc',
      materialName: 'Rebar 24" OC',
      unit: 'ea',
      requiredFields: ['area'],
      // Two-way 24" OC: area × 1 LF/SF ÷ 20 ft/bar × 1.12 waste factor
      calculate: ({ area }) => Math.round(area * 1 / 20 * 1.12),
    }],
  },
  {
    rowId: 'c-r17',
    section: 'concrete',
    materials: [{
      formulaKey: 'saw-cut',
      materialName: 'Saw Cut',
      unit: 'ft',
      requiredFields: ['length'],
      calculate: ({ length }) => length,
    }],
  },
  {
    // "Furnish and place {depth} inches of class 2 base material in an area of
    // {area} sq ft." Same class-2-base tonnage calc as the Asphalt (a-r1),
    // Asphalt R&R (a-sr16) and Grading (g-r4) rows — previously unwired for
    // Concrete, so this scope line produced no material.
    rowId: 'c-r23',
    section: 'concrete',
    materials: [{
      formulaKey: 'class-2-base',
      materialName: 'Class 2 Base',
      unit: 'ton',
      requiredFields: ['depth', 'area'],
      calculate: ({ area, depth }) => volumeToTons(area, depth, DENSITY.CLASS_2_BASE),
    }],
  },
  // c-r18: Remove curb — removal only
  // c-r19: Remove curb and gutter — removal only
  // c-r20: Remove rolled curb — removal only
  // c-r21: Remove concrete by area/depth — removal only (writeup text no longer mentions EV stalls)

  // ═══════════════════════════════════════════
  // SEAL COAT
  // ═══════════════════════════════════════════

  // sc-r1: Clean with blowers — no material
  // sc-r2: removed — replaced by sc-r5 (explicit dimensions)
  // sc-r3: Mastic — fixed price, no variable material
  {
    rowId: 'sc-r4',
    section: 'seal-coat',
    materials: [{
      formulaKey: 'crack-fill-cold',
      materialName: 'Crack Fill - Cold',
      unit: 'gallon',
      requiredFields: ['length', 'depth', 'width'],
      calculate: ({ length, depth, width }) =>
        length * (depth / 12) * (width / 12) * DENSITY.CRACK_FILL_COLD,
    }],
  },
  {
    rowId: 'sc-r5',
    section: 'seal-coat',
    materials: [{
      formulaKey: 'crack-fill-hot',
      materialName: 'Crack Fill - Hot',
      unit: 'Each',
      requiredFields: ['length', 'depth', 'width'],
      calculate: ({ length, depth, width }) =>
        length * (depth / 12) * (width / 12) * DENSITY.CRACK_FILL_HOT,
    }],
  },
  // sc-r6: Treat oil spots — no variable material
  {
    rowId: 'sc-r7',
    section: 'seal-coat',
    materials: [{
      formulaKey: 'sealcoat',
      materialName: 'Sealcoat',
      unit: 'gallon',
      requiredFields: ['coats', 'area'],
      calculate: ({ coats, area }, stringFields) => {
        const surfaceType = stringFields?.surface || 'S'
        const coatKey = coats >= 2 ? 2 : 1
        const rate = SEALCOAT_RATE[surfaceType]?.[ coatKey] ?? SEALCOAT_RATE['S'][coatKey]
        return area * rate
      },
    }],
  },
  {
    rowId: 'sc-r8',
    section: 'seal-coat',
    materials: [{
      formulaKey: 'sealcoat-2pct-latex',
      materialName: 'Sealcoat w/ 2% Latex',
      unit: 'Gallon',
      requiredFields: ['coats', 'area'],
      calculate: ({ coats, area }, stringFields) => {
        const surfaceType = stringFields?.surface || 'S'
        const coatKey = coats >= 2 ? 2 : 1
        const rate = SEALCOAT_RATE[surfaceType]?.[coatKey] ?? SEALCOAT_RATE['S'][coatKey]
        return area * rate
      },
    }],
  },
]

// ── Lookup index for fast access ──

const registryByRowId = new Map<string, FormulaEntry>()
for (const entry of FORMULA_REGISTRY) {
  registryByRowId.set(entry.rowId, entry)
}

export function getFormulaForRow(rowId: string): FormulaEntry | undefined {
  return registryByRowId.get(rowId)
}

export const ALL_REGISTRY_FORMULA_KEYS: Set<string> = new Set(
  FORMULA_REGISTRY.flatMap((e) => e.materials.map((m) => m.formulaKey))
)

export function getFormulasForSection(section: string): FormulaEntry[] {
  return FORMULA_REGISTRY.filter((f) => f.section === section)
}

// ── Field extraction from writeup row content ──

interface WriteupField {
  value: string
  size: string
  placeholder?: string
}

type WriteupContentItem = string | WriteupField

export function extractFieldValues(content: WriteupContentItem[]): Record<string, number> {
  const fields: Record<string, number> = {}
  for (const item of content) {
    if (typeof item !== 'string' && item.placeholder && item.value) {
      const num = parseFloat(item.value.replace(/,/g, ''))
      if (!isNaN(num) && num > 0) {
        fields[item.placeholder] = num
      }
    }
  }
  return fields
}

/** Extract ALL field values including non-numeric strings (e.g., surface type dropdown) */
export function extractAllFieldValues(content: WriteupContentItem[]): Record<string, string> {
  const fields: Record<string, string> = {}
  for (const item of content) {
    if (typeof item !== 'string' && item.placeholder && item.value) {
      fields[item.placeholder] = item.value
    }
  }
  return fields
}

// ── Main calculation function ──

interface WriteupRow {
  id: string
  content: WriteupContentItem[]
  enabled: boolean
}

interface WriteupSection {
  id: string
  title?: string
  rows: WriteupRow[]
}

interface WriteupGroup {
  id: string
  title?: string
  rows: WriteupRow[]
  sections: WriteupSection[]
}

/**
 * Calculate all material quantities from enabled writeup rows.
 * Returns an array of MaterialCalculation objects grouped by section.
 *
 * @param scopeData - JSON-serialized WriteupGroup[] from the estimate
 * @param section - Optional: filter to a specific section (e.g., 'asphalt')
 */
export function calculateMaterialsFromWriteup(
  scopeData: string | undefined,
  section?: string
): MaterialCalculation[] {
  if (!scopeData) return []

  let groups: WriteupGroup[]
  try {
    groups = JSON.parse(scopeData)
  } catch {
    return []
  }

  const results: MaterialCalculation[] = []

  for (const group of groups) {
    // Process top-level rows
    processRows(group.rows, results)

    // Process nested section rows
    for (const sec of group.sections || []) {
      processRows(sec.rows, results)
    }
  }

  // Filter by section if requested
  if (section) {
    return results.filter((r) => {
      const formula = registryByRowId.get(r.sourceRowId)
      return formula?.section === section
    })
  }

  return results
}

function processRows(rows: WriteupRow[], results: MaterialCalculation[]): void {
  for (const row of rows) {
    if (!row.enabled) continue

    // Strip duplicate suffix to find the base formula. Handles both a single
    // duplicate ("a-sr13-copy-1716000000000") and chained copies of copies
    // ("a-sr13-copy-1716...-copy-1716...-copy-1716...").
    const baseRowId = row.id.replace(/(-copy-\d+)+$/, '')
    const formula = registryByRowId.get(baseRowId)
    if (!formula) continue

    const fields = extractFieldValues(row.content)
    const stringFields = extractAllFieldValues(row.content)

    for (const mat of formula.materials) {
      // Check all required fields have values
      const hasAllFields = mat.requiredFields.every((f) => fields[f] !== undefined)
      if (!hasAllFields) continue

      const quantity = mat.calculate(fields, stringFields)
      if (quantity > 0) {
        results.push({
          formulaKey: mat.formulaKey,
          materialName: mat.materialName,
          quantity: Math.round(quantity * 100) / 100, // Round to 2 decimal places
          unit: mat.unit,
          sourceRowId: baseRowId,
        })
      }
    }
  }
}

/**
 * Reverse lookup: given a formulaKey and section, returns all writeup row IDs
 * that produce that material. Used to uncheck writeup rows when a user manually
 * deletes a formula-driven material line item.
 */
export function getWriteupRowIdsForMaterial(formulaKey: string, section: string): string[] {
  const formulas = getFormulasForSection(section)
  const rowIds: string[] = []
  for (const entry of formulas) {
    for (const mat of entry.materials) {
      if (mat.formulaKey === formulaKey) {
        rowIds.push(entry.rowId)
      }
    }
  }
  return rowIds
}

/**
 * Get the set of all unique formulaKeys used in a given section.
 */
export function getFormulaKeysForSection(section: string): Set<string> {
  const keys = new Set<string>()
  for (const entry of getFormulasForSection(section)) {
    for (const mat of entry.materials) {
      keys.add(mat.formulaKey)
    }
  }
  return keys
}

// ── Breakdown lookup: scope inputs that produced a given material ──

/**
 * Human-readable labels and units for writeup row field placeholders
 * (the `placeholder` values on writeup field objects).
 * Used by the material breakdown tooltip in the asphalt/concrete/sealcoat tabs.
 */
export const WRITEUP_FIELD_DISPLAY: Record<string, { label: string; unit: string }> = {
  area: { label: 'Area', unit: 'sf' },
  depth: { label: 'Depth', unit: 'in' },
  length: { label: 'Length', unit: 'ft' },
  numCoats: { label: 'Coats', unit: '' },
  surfaceType: { label: 'Surface', unit: '' },
}

export interface MaterialBreakdownEntry {
  rowId: string                                 // Writeup row ID (e.g., 'a-r1')
  groupTitle: string                            // e.g., "Asphalt"
  sectionTitle?: string                         // e.g., "Remove & Replace" (only if in a sub-section)
  fields: { key: string; value: number | string }[]  // Ordered field inputs that drove this quantity
  quantity: number                              // This row's contribution to the material quantity
  unit: string                                  // Material's unit (ton, gallon, etc.)
}

/**
 * For a single line item (identified by formulaKey + section), return the list
 * of enabled writeup rows that contributed quantity to it — along with the
 * fields that drove each row's calculation and the resulting quantity.
 *
 * Returns an empty array when scopeData is missing, the formula key is unknown,
 * or no enabled writeup row in the section produces this material.
 */
export function getMaterialBreakdown(
  scopeData: string | undefined,
  formulaKey: string,
  section: string,
): MaterialBreakdownEntry[] {
  if (!scopeData) return []
  let groups: WriteupGroup[]
  try {
    groups = JSON.parse(scopeData)
  } catch {
    return []
  }

  const out: MaterialBreakdownEntry[] = []

  const visit = (row: WriteupRow, groupTitle: string, sectionTitle?: string) => {
    if (!row.enabled) return
    const baseRowId = row.id.replace(/(-copy-\d+)+$/, '')
    const formula = registryByRowId.get(baseRowId)
    if (!formula || formula.section !== section) return

    const fields = extractFieldValues(row.content)
    const stringFields = extractAllFieldValues(row.content)

    for (const mat of formula.materials) {
      if (mat.formulaKey !== formulaKey) continue
      const hasAllFields = mat.requiredFields.every((f) => fields[f] !== undefined)
      if (!hasAllFields) continue
      const quantity = mat.calculate(fields, stringFields)
      if (quantity <= 0) continue

      // Build an ordered field list preferring numeric fields in declared order,
      // then any string fields (e.g., surfaceType dropdown) that were present.
      const orderedFields: { key: string; value: number | string }[] = []
      for (const k of mat.requiredFields) {
        if (fields[k] !== undefined) orderedFields.push({ key: k, value: fields[k] })
        else if (stringFields[k] !== undefined) orderedFields.push({ key: k, value: stringFields[k] })
      }
      // Include any extra string fields that weren't required (e.g., surfaceType
      // for sealcoat is consumed by calculate but isn't always in requiredFields).
      for (const [k, v] of Object.entries(stringFields)) {
        if (orderedFields.find((f) => f.key === k)) continue
        if (fields[k] !== undefined) continue
        orderedFields.push({ key: k, value: v })
      }

      out.push({
        rowId: baseRowId,
        groupTitle,
        sectionTitle,
        fields: orderedFields,
        quantity: Math.round(quantity * 100) / 100,
        unit: mat.unit,
      })
    }
  }

  for (const group of groups) {
    const groupTitle = group.title || group.id
    for (const row of group.rows || []) visit(row, groupTitle)
    for (const sec of group.sections || []) {
      const secTitle = sec.title || sec.id
      for (const row of sec.rows || []) visit(row, groupTitle, secTitle)
    }
  }

  return out
}
